import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Breadcrumbs,
  Container,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import { ChevronRight, MoreHorizontal } from "lucide-react";
import { useAuth } from "@context/auth";
import { useMatches, useLocation, useNavigate } from "react-router-dom";

import NavigationButtonCst from "@components/NavigationButtonCst";
import { theme } from "src/theme";
import { useTranslation } from "react-i18next";
import { log } from "console";

const V2_SEGMENTS_TO_SKIP = new Set(["v2", "detail"]);
const V2_SEGMENTS_REDIRECTED_TO_CATALOG = new Set([
  "activities",
  "courses",
  "paths",
]);

const Breadcrumb: React.FC<{ crumbs?: { to: string; text: string }[] }> = ({
  crumbs,
}) => {
  const { user } = useAuth();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation(["breadcrumb"]);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null);

  useEffect(() => {
    setMoreAnchor(null);
  }, [location.pathname]);

  let newCrumbs = location.pathname.split("/").filter((crumb) => crumb !== "");

  const isV2 = location.pathname.startsWith("/v2/");
  const v2CorporateId =
    isV2 && newCrumbs[1] === "corporate" ? newCrumbs[2] : undefined;
  const v2CatalogPath = v2CorporateId
    ? `/v2/corporate/${v2CorporateId}/detail/catalog`
    : "";

  const [scrollClass, setScrollClass] = useState("");
  let lastScrollTop = 0;

  const handleWheel = (e: any) => {
    var actualScrollTop =
      window.pageYOffset || document.documentElement.scrollTop;
    if (actualScrollTop > lastScrollTop) {
      actualScrollTop > 100
        ? setScrollClass("fixed-under")
        : setScrollClass("");
    } else if (actualScrollTop < lastScrollTop) {
      actualScrollTop > 100 ? setScrollClass("fixed-top") : setScrollClass("");
    }
    lastScrollTop = actualScrollTop <= 0 ? 0 : actualScrollTop;
  };

  useEffect(() => {
    window.addEventListener("scroll", (e: any) => {
      handleWheel(e);
    });
  }, []);

  let wordsToRemoveFromBreadCrumb = [
    "SYNC",
    "ASYNC",
    "BLENDED",
    "DA_SCORM",
    "DA_XAPI",
    "CUSTOM",
    "null",
    "DA_PHYSICAL_CLASS",
    "DA_VIRTUAL_CLASS",
    "DA_EXTERNAL_RES",
    "DA_YOUTUBE",
    "DA_LINKEDIN",
    "DA_LTI",
    "DA_SURVEY",
    "DA_URL_RES",
    "COURSE",
    "PATH",
  ];

  const crumbItems = useMemo(() => {
    let currentLink = "";
    const items: { key: number; label: string; to?: string }[] = [];

    newCrumbs.forEach((crumb, index) => {
      currentLink += `/${crumb}`;

      // If user is not Product Owner it is rendered Corporate Menu
      // don't show corporates table link inside Breadcrumb
      if (
        user &&
        // @ts-ignore
        !user.roles?.includes("PRODUCT_OWNER") &&
        crumb === "corporate"
      ) {
        return;
      }

      if (isV2 && crumb === "corporate") {
        return;
      }

      if (Number(crumb) > 0) {
        return;
      }

      if (wordsToRemoveFromBreadCrumb.includes(crumb)) {
        return;
      }

      if (isV2 && V2_SEGMENTS_TO_SKIP.has(crumb)) {
        return;
      }

      const isLast = index === newCrumbs.length - 1;

      const v2LabelKey =
        isV2 && crumb === "catalog" ? "offerta-formativa" : crumb;
      const v2UsersGroupsPath = v2CorporateId
        ? `/v2/corporate/${v2CorporateId}/detail/users-groups`
        : "";
      const linkOverride =
        isV2 && V2_SEGMENTS_REDIRECTED_TO_CATALOG.has(crumb)
          ? `${v2CatalogPath}?tab=${crumb}`
          : isV2 && crumb === "drafts"
            ? `${v2CatalogPath}?tab=all`
            : isV2 && crumb === "groups"
              ? `${v2UsersGroupsPath}?tab=static-groups`
              : isV2 && crumb === "dynamic-groups"
                ? `${v2UsersGroupsPath}?tab=dynamic-groups`
                : undefined;

      items.push({
        key: index,
        label: t(v2LabelKey),
        to:
          isLast || crumb === "edit"
            ? undefined
            : (linkOverride ?? currentLink),
      });
    });

    (crumbs ?? []).forEach((crumb, index, extra) => {
      items.push({
        key: newCrumbs.length + index,
        label: crumb.text,
        to: index === extra.length - 1 ? undefined : crumb.to || undefined,
      });
    });

    return items;
  }, [newCrumbs, isV2, v2CatalogPath, t, user, crumbs]);

  if (isV2 && crumbItems.length < 2) {
    return null;
  }

  const renderItem = crumbItems.map((item) =>
    item.to ? (
      <NavigationButtonCst key={item.key} to={item.to}>
        <Typography
          variant="supportingTextL"
          sx={{
            textTransform: "capitalize",
            ...(isV2 && {
              fontSize: "12px",
              fontWeight: 300,
              lineHeight: "18px",
            }),
          }}
        >
          {item.label}
        </Typography>
      </NavigationButtonCst>
    ) : (
      <Typography
        key={item.key}
        variant="supportingTextL"
        sx={
          isV2
            ? {
                fontWeight: 600,
                fontSize: "12px",
                lineHeight: "18px",
                display: "flex",
                alignItems: "center",
              }
            : { fontWeight: undefined }
        }
      >
        {item.label}
      </Typography>
    ),
  );

  return (
    <Box
      sx={{
        pb: "1.5rem",
        position: "sticky",
        top: "50px",
        transition: "top 0.7s ease ",
        "&.fixed-top, &.fixed-under": {
          zIndex: "99",
          backgroundColor: isV2
            ? theme.palette.background.default
            : theme.palette.background.paper,
          borderTop: "0.5px solid rgba(55, 55, 55, 0.4)",
          transition: "top 0.7s ease ",
        },
        "&.fixed-top": {
          top: "50px",
        },
        "&.fixed-under": {
          top: "90px",
        },
      }}
      className={scrollClass === "" ? "" : scrollClass}
    >
      <Container
        maxWidth={"xxl"}
        disableGutters={isV2}
        sx={{
          position: "relative",
          zIndex: 1,
          ...(isV2 && { px: { xs: 0, md: 0 } }),
        }}
      >
        {isMobile && crumbItems.length >= (isV2 ? 3 : 2) ? (
          <>
            <Breadcrumbs
              maxItems={10}
              separator={isV2 ? <ChevronRight size={14} /> : "/"}
              aria-label="breadcrumb"
            >
              {isV2 ? (
                renderItem[0]
              ) : (
                <NavigationButtonCst to={"/"}>
                  <Typography
                    variant="supportingTextL"
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <HomeIcon sx={{ mr: 0.5 }} />
                    {t("home")}
                  </Typography>
                </NavigationButtonCst>
              )}
              <Typography
                variant="supportingTextL"
                onClick={(e: React.MouseEvent<HTMLElement>) =>
                  setMoreAnchor(e.currentTarget)
                }
                aria-label="show intermediate steps"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  color: theme.palette.text.primary,
                }}
              >
                <MoreHorizontal size={18} />
              </Typography>
              {renderItem[renderItem.length - 1]}
            </Breadcrumbs>
            <Menu
              anchorEl={moreAnchor}
              open={!!moreAnchor}
              onClose={() => setMoreAnchor(null)}
              PaperProps={{
                sx: {
                  borderRadius: "12px",
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.background.paper,
                  minWidth: "220px",
                },
              }}
            >
              {crumbItems.slice(isV2 ? 1 : 0, -1).map((item) => (
                <MenuItem
                  key={item.key}
                  disabled={!item.to}
                  onClick={() => {
                    setMoreAnchor(null);
                    if (item.to) navigate(item.to);
                  }}
                  sx={{ gap: 1, paddingY: "10px" }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: "unset",
                      color: theme.palette.text.primary,
                    }}
                  >
                    <ChevronRight size={16} />
                  </ListItemIcon>
                  <Typography
                    variant="supportingTextL"
                    sx={{
                      fontWeight: isV2 ? 600 : 700,
                      textTransform: "capitalize",
                      color: theme.palette.text.primary,
                      ...(isV2 && { fontSize: "12px" }),
                    }}
                  >
                    {item.label}
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
          </>
        ) : (
          <Breadcrumbs
            maxItems={5}
            itemsAfterCollapse={2}
            separator={isV2 ? <ChevronRight size={14} /> : "/"}
            aria-label="breadcrumb"
          >
            {!isV2 && (
              <NavigationButtonCst to={"/"}>
                <Typography
                  variant="supportingTextL"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <HomeIcon sx={{ mr: 0.5 }} />
                  {t("home")}
                </Typography>
              </NavigationButtonCst>
            )}
            {renderItem}
          </Breadcrumbs>
        )}
      </Container>
    </Box>
  );
};

export default Breadcrumb;
